/* Вход через Keycloak: Authorization Code + PKCE.
   Разбор архитектуры — вольт 03_System/Security/Аутентификация.

   Реализация делегирована oidc-client-ts: свой PKCE и разбор токенов — типичный
   источник уязвимостей. Секрет клиента в браузер не кладётся: в SPA его негде
   спрятать, поэтому клиент публичный. */
import { UserManager, WebStorageStateStore, type User } from 'oidc-client-ts'

const AUTHORITY = (import.meta.env.VITE_OIDC_AUTHORITY as string | undefined) ?? 'http://localhost:8081/realms/tdms'
const CLIENT_ID = (import.meta.env.VITE_OIDC_CLIENT_ID as string | undefined) ?? 'tdms-web'

export const userManager = new UserManager({
  authority: AUTHORITY,
  client_id: CLIENT_ID,
  redirect_uri: window.location.origin + '/',
  post_logout_redirect_uri: window.location.origin + '/',
  response_type: 'code',
  scope: 'openid profile email',
  // sessionStorage, а не localStorage: сессия не переживает закрытие вкладки
  // и не растекается по другим вкладкам браузера.
  userStore: new WebStorageStateStore({ store: window.sessionStorage }),
  automaticSilentRenew: true,
  // Токен из адресной строки убирается сразу после обмена, чтобы не осесть
  // в истории браузера и в логах прокси.
  monitorSession: false,
})

/** Кто вошёл. Роль и вендор берутся ИЗ ТОКЕНА — не из выбора в интерфейсе. */
export interface Session {
  subject: string
  name: string
  /** Заполнен только у внешнего участника; им же ограничена видимость документов. */
  vendor: string | null
  /**
   * Проекты, разрешённые токеном. Сотрудник может работать на нескольких
   * стройках; активный выбирается в интерфейсе и уходит заголовком, но выбрать
   * можно только из этого списка — проверяет сервер.
   */
  projects: string[]
  roles: string[]
  accessToken: string
}

export interface AccessClaims {
  sub?: string
  name?: string
  preferred_username?: string
  vendor?: string
  /** Многозначный атрибут Keycloak приходит массивом; одиночный — строкой. */
  project?: string | string[]
  realm_access?: { roles?: string[] }
}

/** base64url → строка UTF-8. Имена вендоров кириллические, побайтовое чтение их искажает. */
export function decodeBase64Url(input: string): string {
  const b64 = input.replace(/-/g, '+').replace(/_/g, '/')
  const padded = b64 + '='.repeat((4 - (b64.length % 4)) % 4)
  const binary = atob(padded)
  return new TextDecoder().decode(Uint8Array.from(binary, (ch) => ch.charCodeAt(0)))
}

/** Полезная нагрузка access-токена. Подпись проверяет сервер, клиенту она нужна только для UI. */
export function claimsOf(token: string): AccessClaims {
  const payload = token.split('.')[1]
  if (!payload) return {}
  try {
    return JSON.parse(decodeBase64Url(payload)) as AccessClaims
  } catch {
    return {}
  }
}

export function toSession(user: User): Session {
  const c = claimsOf(user.access_token)
  return {
    subject: c.sub ?? '',
    name: c.name || c.preferred_username || '',
    vendor: c.vendor && c.vendor.trim() ? c.vendor : null,
    projects: projectsOf(c.project),
    roles: c.realm_access?.roles ?? [],
    accessToken: user.access_token,
  }
}

/** Утверждение project: массив, одиночная строка или ничего — всегда список. */
export function projectsOf(claim: string | string[] | undefined): string[] {
  const list = claim == null ? [] : Array.isArray(claim) ? claim : [claim]
  return list.map((p) => String(p).trim()).filter((p) => p !== '')
}

/* Код авторизации одноразовый: повторный обмен возвращает 400. В режиме
   разработки React StrictMode монтирует эффекты дважды, поэтому попытка обмена
   должна быть ровно одна на загрузку страницы — держим её обещание здесь. */
let pending: Promise<Session | null> | null = null

/**
 * Возврат из Keycloak, если в адресе есть code; иначе — восстановление сессии.
 * Возвращает null, когда пользователь не вошёл.
 */
export function restoreSession(): Promise<Session | null> {
  pending ??= exchangeOrRestore()
  return pending
}

/* Куда человек шёл до входа. Keycloak возвращает на один и тот же
   redirect_uri, поэтому ссылка, открытая без сессии, теряла адрес: получатель
   ссылки на разбор трансмиттала попадал на «Мои действия» и искал его сам. */
const RETURN_TO = 'tdms_return_to'

async function exchangeOrRestore(): Promise<Session | null> {
  const params = new URLSearchParams(window.location.search)
  if (params.has('code') && params.has('state')) {
    const user = await userManager.signinRedirectCallback()
    // Код убирается из адреса сразу: иначе он осядет в истории браузера
    // и в логах прокси, а перезагрузка страницы вызовет повторный обмен.
    // Заодно восстанавливается исходный путь, ради которого и шли.
    const back = sessionStorage.getItem(RETURN_TO)
    sessionStorage.removeItem(RETURN_TO)
    window.history.replaceState({}, '', back || window.location.pathname)
    return toSession(user)
  }
  const user = await userManager.getUser()
  return user && !user.expired ? toSession(user) : null
}

/**
 * Немедленное обновление токена (refresh-грант). Нужен, когда claim'ы
 * изменились на сервере — например, создателю проекта только что выдали
 * доступ: без обновления новый проект появится в токене лишь при плановом
 * silent renew. Событие userLoaded само доставит сессию в useSession.
 */
export async function refreshSession(): Promise<Session | null> {
  const user = await userManager.signinSilent()
  return user ? toSession(user) : null
}

export const signIn = (): Promise<void> => {
  const here = window.location.pathname + window.location.search
  if (here !== '/') sessionStorage.setItem(RETURN_TO, here)
  return userManager.signinRedirect()
}

export async function signOut(): Promise<void> {
  await userManager.signoutRedirect()
}
